import { useState, useEffect } from 'react';
import { api, invalidateCached } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, ExternalLink } from 'lucide-react';

export default function AdminFooterSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/content/footer_link').then(r => setData(r.data)).catch(() => {
      setData({ creator_name: 'IG DESIGN LAB', creator_url: '' });
    });
  }, []);

  const handleSave = async () => {
    if (!data.creator_name) { toast.error('Creator name is required'); return; }
    setSaving(true);
    try {
      await api.put('/admin/content/footer_link', data);
      invalidateCached('/content/footer_link');
      toast.success('Footer settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (!data) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#2D5BE3] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-footer-settings-page">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Footer Settings
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Update the creator credit link shown in the website footer</p>
      </div>

      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Creator Name</label>
          <Input
            data-testid="footer-creator-name-input"
            value={data.creator_name}
            onChange={(e) => setData({ ...data, creator_name: e.target.value })}
            placeholder="IG DESIGN LAB"
            className="rounded-xl"
          />
          <p className="text-xs text-[#94A3B8] mt-1">Displayed as the clickable name in the footer copyright line</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-1">Creator Link URL</label>
          <Input
            data-testid="footer-creator-url-input"
            value={data.creator_url}
            onChange={(e) => setData({ ...data, creator_url: e.target.value })}
            placeholder="https://wa.me/50937007500?text=..."
            className="rounded-xl"
          />
          <p className="text-xs text-[#94A3B8] mt-1">WhatsApp link, website, or any URL. Opens in a new tab.</p>
        </div>

        {/* Preview */}
        <div className="bg-[#0F172A] rounded-xl p-5">
          <p className="text-[#94A3B8] text-sm text-center">
            &copy; Million Stars Community Ministry &mdash; Created by{' '}
            <a href={data.creator_url || '#'} target="_blank" rel="noopener noreferrer"
              className="text-[#60A5FA] hover:text-white font-semibold transition-colors inline-flex items-center gap-1">
              {data.creator_name || 'IG DESIGN LAB'} <ExternalLink size={12} />
            </a>
          </p>
          <p className="text-[#475569] text-xs text-center mt-2">Live preview</p>
        </div>

        <div className="flex justify-end pt-2">
          <Button data-testid="save-footer-settings-btn" onClick={handleSave} disabled={saving}
            className="bg-[#2D5BE3] hover:bg-[#1E4FA1] text-white rounded-xl px-6">
            <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Footer Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}
